import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, getApiAuthHeaders, licenseService } from '../services/api';
import toast from 'react-hot-toast';
import { TENANT_ORG_ID_STORAGE_KEY } from '../constants/tenant';
import { useTenant } from '../contexts/TenantContext';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../i18n/userFacingError';

const FORMAT_LABELS = { pdf: 'PDF', xlsx: 'Excel', csv: 'CSV', html: 'HTML', docx: 'Word' };
const PRIMARY = '#388E3C';
const ACCENT = '#2196F3';

function FilterInput({ field, value, onChange }) {
  if (field.field_type === 'select' && field.options?.length) {
    return (
      <select className="w-full border rounded px-3 py-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  const type = field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text';
  return (
    <input className="w-full border rounded px-3 py-2 text-sm" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.label} />
  );
}

export default function ReportCenter() {
  const { t } = useTranslation('reports');

  const { tenant } = useTenant();
  const orgId = tenant?.id || localStorage.getItem(TENANT_ORG_ID_STORAGE_KEY);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({});
  const [exporting, setExporting] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [plano, setPlano] = useState(null);
  const [planoLoading, setPlanoLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const baseUrl = `${API_BASE_URL}/api/v1/reports`;

  const fetchCatalog = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/catalog`, { headers: getApiAuthHeaders({ Accept: 'application/json' }) });
      if (!res.ok) throw Object.assign(new Error('load_failed'), { status: res.status });
      const data = await res.json();
      setCatalog(data.items || []);
    } catch (e) {
      setError(userFacingError(e, 'reports:loadError'));
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, orgId]);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const grouped = useMemo(() => {
    const map = new Map();
    catalog.forEach((r) => { const l = map.get(r.module) || []; l.push(r); map.set(r.module, l); });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);

  const selected = catalog.find((r) => r.id === selectedId);

  const clearPreview = () => {
    setPreviewHtml(null);
    setPreviewError(null);
  };

  const handleSelect = (report) => {
    setSelectedId(report.id);
    const defaults = { organization_id: orgId };
    (report.filter_schema || []).forEach((f) => { if (f.default != null) defaults[f.key] = String(f.default); });
    setFilters(defaults);
    setExportError(null);
    clearPreview();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    clearPreview();
  };

  const handlePreview = async () => {
    if (!selected || !orgId) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewHtml(null);
    try {
      const res = await fetch(`${baseUrl}/${selected.id}/generate`, {
        method: 'POST',
        headers: {
          ...getApiAuthHeaders({ Accept: 'text/html, application/json', 'Content-Type': 'application/json' }),
        },
        body: JSON.stringify({ filters: { organization_id: orgId, ...filters }, preview: true }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error('preview_failed'), { status: res.status });
      }
      setPreviewHtml(await res.text());
    } catch (e) {
      setPreviewError(userFacingError(e, 'reports:previewError'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadReport = async (format) => {
    if (!selected || !orgId) return;
    setExporting(format);
    setExportError(null);
    try {
      const params = new URLSearchParams({ format, organization_id: orgId, ...filters });
      const res = await fetch(`${baseUrl}/${selected.id}/export?${params}`, { headers: getApiAuthHeaders({ Accept: 'application/json' }) });
      if (!res.ok) { await res.json().catch(() => ({})); throw Object.assign(new Error('export_failed'), { status: res.status }); }
      const blob = await res.blob();
      const match = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || `${selected.id}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(userFacingError(e, 'exportError'));
    } finally {
      setExporting(null);
    }
  };

  if (!orgId) return <div className="p-6 text-gray-600">Organização não resolvida.</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-1" style={{ color: PRIMARY }}>Central de Relatórios</h1>
      <p className="text-sm text-gray-500 mb-6">Selecione um relatório, visualize e exporte em PDF, Excel, CSV, HTML e Word.</p>
      {loading && <p>Carregando…</p>}
      {error && <p className="text-red-600">{error}</p>}
      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          <aside className="border rounded-lg p-3 bg-white max-h-[70vh] overflow-auto">
            <button type="button" className="text-xs text-green-700 underline mb-2" onClick={fetchCatalog}>Atualizar</button>
            {grouped.map(([module, reports]) => (
              <div key={module}>
                <div className="text-[11px] font-bold uppercase text-gray-400 mt-2 mb-1">{module}</div>
                {reports.map((r) => (
                  <button key={r.id} type="button" onClick={() => handleSelect(r)}
                    className={`block w-full text-left px-3 py-2 rounded text-sm mb-1 ${selectedId === r.id ? 'bg-green-700 text-white' : 'hover:bg-green-50'}`}>
                    {r.title}
                  </button>
                ))}
              </div>
            ))}
          </aside>
          <main className="border rounded-lg p-5 bg-white min-h-[360px]">
            {!selected ? <p className="text-center text-gray-400 py-12">Selecione um relatório.</p> : (
              <>
                <h2 className="text-lg font-semibold" style={{ color: PRIMARY }}>{selected.title}</h2>
                <p className="text-sm text-gray-500 mt-1">{selected.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                  {(selected.filter_schema || []).filter((f) => f.key !== 'organization_id').map((field) => (
                    <div key={field.key}>
                      <label className="text-[11px] uppercase text-gray-500">{field.label}</label>
                      <FilterInput field={field} value={filters[field.key] ?? ''} onChange={(v) => handleFilterChange(field.key, v)} />
                    </div>
                  ))}
                </div>
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={previewLoading}
                    className="px-5 py-2 rounded text-sm font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {previewLoading ? 'Gerando visualização…' : 'Visualizar'}
                  </button>
                </div>
                {previewError && <p className="text-red-600 text-sm mt-3">{previewError}</p>}
                {previewHtml && (
                  <section className="mt-5">
                    <h3 className="text-xs uppercase text-gray-500 font-bold mb-2">Pré-visualização</h3>
                    <div className="border rounded overflow-hidden bg-white">
                      <iframe
                        title={t("preview")}
                        srcDoc={previewHtml}
                        sandbox="allow-same-origin"
                        className="w-full border-0 block"
                        style={{ minHeight: 480 }}
                      />
                    </div>
                  </section>
                )}
                <div className="mt-5">
                  <h3 className="text-xs uppercase text-gray-500 font-bold mb-2">Exportar</h3>
                  <div className="flex flex-wrap gap-2">
                  {(selected.supported_formats || []).map((fmt) => (
                    <button key={fmt} type="button" disabled={exporting !== null} onClick={() => downloadReport(fmt)}
                      className="px-4 py-2 rounded text-sm font-semibold text-white disabled:opacity-60"
                      style={{ backgroundColor: fmt === 'pdf' ? ACCENT : PRIMARY }}>
                      {exporting === fmt ? 'Exportando…' : `Exportar ${FORMAT_LABELS[fmt] || fmt}`}
                    </button>
                  ))}
                  </div>
                  {!previewHtml && (
                    <p className="text-xs text-gray-400 mt-2">Dica: use Visualizar para conferir o relatório antes de exportar.</p>
                  )}
                </div>
                {exportError && <p className="text-red-600 text-sm mt-3">{exportError}</p>}
                {selected.id === 'compliance.score' ? (
                  <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <h3 className="text-sm font-semibold text-amber-900">Plano de regularização</h3>
                    <p className="mt-1 text-xs text-amber-800">
                      Gera ações a partir de condicionantes pendentes, licenças vencidas e denúncias abertas.{' '}
                      <Link to="/license/regularization-plans" className="underline">
                        Abrir histórico do plano
                      </Link>
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm mt-3"
                      disabled={planoLoading}
                      onClick={async () => {
                        setPlanoLoading(true);
                        try {
                          const { data } = await licenseService.generatePlanoRegularizacao();
                          setPlano(data);
                          toast.success(`Plano gerado. Score ${data.score}%.`);
                        } catch (e) {
                          toast.error(userFacingError(e, 'saveError'));
                        } finally {
                          setPlanoLoading(false);
                        }
                      }}
                    >
                      {planoLoading ? 'Gerando…' : 'Gerar plano'}
                    </button>
                    {plano ? (
                      <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-sm">
                        {(plano.items || []).map((item) => (
                          <li key={item.id} className="flex items-start justify-between gap-2">
                            <span>
                              <strong>{item.tipo}</strong> · {item.titulo}
                              <span className="ml-1 text-xs text-gray-500">({item.status})</span>
                            </span>
                            {item.status !== 'concluido' ? (
                              <button
                                type="button"
                                className="btn btn-outline btn-sm shrink-0"
                                onClick={async () => {
                                  try {
                                    await licenseService.patchPlanoItem(plano.id, item.id, 'concluido');
                                    setPlano((current) => ({
                                      ...current,
                                      items: (current.items || []).map((row) =>
                                        row.id === item.id ? { ...row, status: 'concluido' } : row
                                      ),
                                    }));
                                  } catch (e) {
                                    toast.error(userFacingError(e, 'saveError'));
                                  }
                                }}
                              >
                                Concluir
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
